import { transient, computedFrom } from "aurelia-framework";
import { BindingSignaler } from "aurelia-templating-resources";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../client-validation-system/validations.models";
import { Claims } from "../../../../../../constants/authentication/claims";
import { EmailRecipientTypeEnum, SubtaskCorrespondenceEmailModel, VetClientModel, VettingType } from "../../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import { ClipboardCopyService } from "../../../../../../utilities/clipboard-copy-service";

@transient()
export class contactDetailsDialog {

    model: VetClientModel;
    public hasEditPermission:boolean = false;
    private editing: {[subtaskCorrespondenceEmailId:number]:boolean} = {};
    private deleting: {[subtaskCorrespondenceEmailId:number]:boolean} = {};
    hasAddPermission:boolean= false;
    emails: { email: string, emailRecipientTypeId: number, isShipManager: boolean }[] = [];
    onConfirm: () => void;
    validateEmails: ClientSideValidator;

    constructor(private dialogClientService: DialogClientService,
        private vettingService:VettingService,
        private bindingSignaler:BindingSignaler,
        private notificationClientService:NotificationClientService, 
        private validatorEmails: ClientSideValidator,
        private entityValidationsService: EntityValidationsService,
        private userClaimsService: UserClaimsService,
        private clipboardCopyService: ClipboardCopyService) {
            
    }

    tooltipConfirm(subtaskCorrespondenceEmailId:number){
        this.vettingService.deleteCorrespondeceEmail(subtaskCorrespondenceEmailId).then(() =>{
            this.deleting[subtaskCorrespondenceEmailId] = false; 
            const deletedEmail = this.model.initialEmailsForCorrespondence.filter(x => x.subtaskCorrespondenceEmailId == subtaskCorrespondenceEmailId)[0];
            const deletedEmailIndex = this.model.initialEmailsForCorrespondence.indexOf(deletedEmail);
            this.model.initialEmailsForCorrespondence.splice(deletedEmailIndex, 1);
            this.bindingSignaler.signal("email-removed");
            this.notificationClientService.show("Success", "Email removed successfuly", NotificationType.Success);
        });
    }

    getEmailReceipientType(emailReceipientTypeId: number) {

        emailReceipientTypeId = emailReceipientTypeId ?? EmailRecipientTypeEnum.To;

        return Object.keys(EmailRecipientTypeEnum)[Object.values(EmailRecipientTypeEnum).indexOf(emailReceipientTypeId)].toString();
    }

    setValidator() {
        const rulesHolderEmails = {
            activation: ValidationActivation.Always,
            entityName: "Vet Request Ship Manager Emails",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Email address is required",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Email address is not in correct format",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Email
            }]
        };
        this.validateEmails = this.entityValidationsService.getValidator(this.emails, rulesHolderEmails);
    }

    tooltipCancel(subtaskCorrespondenceEmailId:number){
        this.deleting[subtaskCorrespondenceEmailId] = false; 
    }

    activate(model: { model:VetClientModel, onConfirm: () => void}) {
        this.model = model.model;
        this.onConfirm = model.onConfirm;

        this.hasEditPermission = this.model.vettingTypeId == VettingType.FullVet && this.userClaimsService.hasPermission(Claims.CanEditVetRequestContactDetails);
        this.hasAddPermission = this.model.vettingTypeId == VettingType.FullVet && this.userClaimsService.hasPermission(Claims.CanAddVetRequestContactDetails);
    }

    cancel() {
        this.dialogClientService.hide();
    }

    @computedFrom("item", "item.initialEmailsForCorrespondence", "item.initialEmailsForCorrespondence.length")
    get correspondenceEmails() {
        if (this.model.initialEmailsForCorrespondence)
            return this.model.initialEmailsForCorrespondence.filter(x => !x.isShipManagerEmail);
        return [];
    }

    edit(subtaskCorrespondenceEmailId:number){
        this.editing[subtaskCorrespondenceEmailId] = true;
        this.deleting[subtaskCorrespondenceEmailId]= false; 
        this.bindingSignaler.signal("edit");
    }

    confirm(subtaskEmailCorrespondence:SubtaskCorrespondenceEmailModel){
        this.addValidation();
        this.validatorEmails.validate().then(x=>{
            if(x.valid){
                this.editing[subtaskEmailCorrespondence.subtaskCorrespondenceEmailId] = false;
                this.vettingService.updateCorrespondeceEmail(subtaskEmailCorrespondence.subtaskCorrespondenceEmailId, subtaskEmailCorrespondence.email).then(() =>{
                    this.notificationClientService.show("Success", "Email update successfuly", NotificationType.Success);
                });
            }
        });
    }

    close() {
        if (this.onConfirm)
            this.onConfirm();
        this.dialogClientService.hide();
    }

    private addValidation(){
        const rulesHolderEmails = {
            activation: ValidationActivation.Always,
            entityName: "Sub Task Correspondece Email",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Email address is required",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Email address is not in correct format",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Email
            }]
        };
        
        this.validatorEmails = this.entityValidationsService.getValidator(this.model.initialEmailsForCorrespondence, rulesHolderEmails);
    }

    delete(subtaskCorrespondenceEmailId:number){
        this.deleting[subtaskCorrespondenceEmailId]= true; 
        this.editing[subtaskCorrespondenceEmailId] = false;
    }

    addEmail() {      
        this.emails.push({ email: "", emailRecipientTypeId: EmailRecipientTypeEnum.To, isShipManager: false });
    }

    removeEmail(i) {
        this.emails.splice(i, 1);
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    async submit() {
        this.setValidator();
        const validator = await this.validateEmails.validate();
        if (validator.valid) {
            try {
                const vetCorrespondenceEmails = this.model.initialEmailsForCorrespondence
                .filter(x => !x.isShipManagerEmail)
                    .concat(this.emails);

                await this.vettingService.saveVetCorrespondenceEmails(this.model.vetId, vetCorrespondenceEmails);
                this.notificationClientService.show("Success", "Email added successfully", NotificationType.Success);
                this.close();
            } catch (e) {
                if (!RequestClientService.isExceptionHandled(e)) {
                    this.notificationClientService.show("Error adding emails,please try again", "", NotificationType.Error);
                }
            }
        }
    }
}
